import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { FeedEntry } from '~/services/layout/index.ts';
import { filterSkeletonEntries } from '~/services/layout/layout';

/**
 * Manages feed entries lifecycle when circular scrolling is enabled.
 *
 * Responsibilities:
 * - Duplicates feed entries before initial render for seamless circular scrolling
 * - Automatically disables circular scrolling when pagination exists (nextFeedUrl)
 * - Preserves paginated entries from being overwritten
 * - Returns shouldEnableCircular flag for coordinating with other hooks
 *
 * Architecture decision: Pagination and circular scrolling are mutually exclusive.
 * When nextFeedUrl exists, circular scrolling is disabled entirely to prevent
 * complexity around re-duplicating paginated entries.
 *
 * @param baseFeedEntries - Original entries from the feed (non-duplicated)
 * @param circularCondition - Whether circular scrolling should be enabled (from config/autoscroll)
 * @param isLoading - Server/feed loading state
 * @param nextFeedUrl - Pagination URL (circular is disabled if present)
 * @returns Object containing feedEntries, originalFeedEntriesLength, shouldEnableCircular, and setFeedEntries
 */
export function useCircularFeedEntries({
  baseFeedEntries,
  circularCondition,
  isLoading,
  nextFeedUrl,
}: {
  baseFeedEntries: FeedEntry[];
  circularCondition: boolean;
  isLoading: boolean | undefined;
  nextFeedUrl: string | undefined;
}): {
  feedEntries: FeedEntry[];
  originalFeedEntriesLength: number;
  shouldEnableCircular: boolean;
  setFeedEntries: Dispatch<SetStateAction<FeedEntry[]>>;
} {
  // Pagination takes precedence - disable circular entirely when pagination exists
  const shouldEnableCircular = circularCondition && !nextFeedUrl;

  /**
   * Duplicates entries for circular scrolling by creating copies with unique keys.
   * Duplicated entries are appended after originals: [1,2,3,1*,2*,3*]
   */
  const duplicateEntriesForCircular = (entries: FeedEntry[]): FeedEntry[] => {
    const duplicatedEntries = entries.map((entry, i) => {
      const _key = `duplicated-entry-${i}`;
      return {
        ...entry,
        _key,
        id: _key,
      };
    });
    return [...entries, ...duplicatedEntries];
  };

  /**
   * Initialize with duplicated entries if circular should be enabled.
   * This happens before initial render to prevent visual flash.
   */
  const initialFeedEntries = useMemo(() => {
    if (shouldEnableCircular && baseFeedEntries.length > 0) {
      return duplicateEntriesForCircular(baseFeedEntries);
    }
    return baseFeedEntries;
  }, [baseFeedEntries, shouldEnableCircular]);

  const [feedEntries, setFeedEntries] =
    useState<FeedEntry[]>(initialFeedEntries);
  const previousInitialFeedEntriesRef = useRef<FeedEntry[]>(initialFeedEntries);
  const didInitialFeedEntriesChange: boolean =
    previousInitialFeedEntriesRef.current !== initialFeedEntries;

  /**
   * Sync with base entries when they change (e.g., preference editor updates)
   * or when another hook replaces the duplicated circular list with the base feed.
   * In-flight pagination skeletons stay until that base snapshot changes.
   * Real appended pages are kept across base updates.
   */
  useEffect(() => {
    if (isLoading) return;

    previousInitialFeedEntriesRef.current = initialFeedEntries;

    setFeedEntries((currentEntries: FeedEntry[]) => {
      const appendedEntries: FeedEntry[] = currentEntries.slice(
        initialFeedEntries.length
      );
      const hasPaginatedEntries: boolean =
        filterSkeletonEntries(appendedEntries).length > 0;

      if (hasPaginatedEntries) return currentEntries;

      const hasOnlySkeletonAppends: boolean =
        !didInitialFeedEntriesChange &&
        appendedEntries.length > 0 &&
        currentEntries.length > initialFeedEntries.length;

      if (hasOnlySkeletonAppends || currentEntries === initialFeedEntries) {
        return currentEntries;
      }

      return initialFeedEntries;
    });
  }, [
    didInitialFeedEntriesChange,
    initialFeedEntries,
    feedEntries.length,
    isLoading,
  ]);

  return {
    feedEntries,
    originalFeedEntriesLength: baseFeedEntries.length,
    shouldEnableCircular,
    setFeedEntries,
  };
}
